import { breadboxAPI, cached } from "@depmap/api";
import { SliceQuery } from "@depmap/types";

// WORKAROUND: This works much like the Breadbox API function
// `getDimensionData` but leverages a few different endpoints, which have
// mostly overlapping functionality but don't include labels in the response.
// THe idea is to limit the size of the response as much as possible. Dimension
// types with 100K+ features, such as transcripts, present a particular
// challenge. The responses are sometimes so large that nginx refuses to serve
// them.
export async function getDimensionDataWithoutLabels(slice: SliceQuery) {
  // Chain queries (with reindex_through) must use the chain-aware endpoint,
  // which resolves FK joins server-side. The response includes labels we don't
  // need, but chain queries are metadata/annotation columns in practice — not
  // 100K-element transcript slices — so the overhead is acceptable.
  if (slice.reindex_through) {
    const { ids, values } = await cached(breadboxAPI, {
      persist: true,
    }).getDimensionData(slice);
    return { ids, values };
  }

  if (slice.identifier_type === "column") {
    const wrapper = await cached(breadboxAPI, {
      persist: true,
    }).getTabularDatasetData(slice.dataset_id, {
      columns: [slice.identifier],
    });

    const indexedData = wrapper[slice.identifier];

    if (!indexedData) {
      throw new Error(
        `Column "${slice.identifier}" not found in dataset "${slice.dataset_id}".`
      );
    }

    return {
      ids: Object.keys(indexedData),
      values: Object.values(indexedData),
    };
  }

  if (["feature_id", "feature_label"].includes(slice.identifier_type)) {
    const features = await cached(breadboxAPI, {
      persist: true,
    }).getMatrixDatasetData(slice.dataset_id, {
      feature_identifier:
        slice.identifier_type === "feature_id" ? "id" : "label",
      features: [slice.identifier],
    });

    if (Object.keys(features).length === 0) {
      return { ids: [] as string[], values: [] as any[] };
    }

    // The response is always an object keyed with feature IDs.
    // In this case, we've only requested one feature.
    const onlyFeatureId = Object.keys(features)[0];
    const valuesBySampleId = features[onlyFeatureId];

    return {
      ids: Object.keys(valuesBySampleId),
      values: Object.values(valuesBySampleId),
    };
  }

  if (["sample_id", "sample_label"].includes(slice.identifier_type)) {
    const features = await cached(breadboxAPI, {
      persist: true,
    }).getMatrixDatasetData(slice.dataset_id, {
      sample_identifier: slice.identifier_type === "sample_id" ? "id" : "label",
      samples: [slice.identifier],
    });

    if (Object.keys(features).length === 0) {
      return { ids: [] as string[], values: [] as any[] };
    }

    // The response is always an object keyed with feature IDs.
    const ids = Object.keys(features);
    // Each value of `features` is itself an object with
    // only one key (the single sample we requested).
    const onlySampleId = Object.keys(features[ids[0]])[0];

    return {
      ids,
      values: Object.values(features).map(
        (valueForSampleId) => valueForSampleId[onlySampleId]
      ),
    };
  }

  throw new Error(`Bad slice query ${JSON.stringify(slice)}`);
}

// Starts requests whose results are awaited again, later, by the caller (see
// the "Pre-fetch" comments). A rejection here isn't an unhandled one: the same
// failure surfaces at the later `await`. But the promise returned here has no
// handler of its own, and an unhandled rejection is only a console message in a
// browser while in Node it ends the process.
export function prefetch(...requests: Promise<unknown>[]) {
  requests.forEach((request) => request.catch(() => {}));
}
